import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

type IdAlias = string | number | null;

const apiUrl = import.meta.env.VITE_API_BASE_URL;

const client: AxiosInstance = axios.create({
  baseURL: apiUrl,
  timeout: 300000,
});

client.interceptors.request.use(async (config) => {
  const { useAuthStore } = await import('../stores/useAuthStore.ts');
  const auth = useAuthStore();
  if (auth.token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${auth.token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { useAuthStore } = await import('../stores/useAuthStore.ts');
      const auth = useAuthStore();

      // refreshTokenOrLogin() dijeli in-flight guard sa router guardom (useAuthStore.ts) —
      // nema potrebe za dodatnim lokalnim dedupom ovdje.
      await auth.refreshTokenOrLogin();

      if (auth.token) {
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${auth.token}`;
        return client(originalRequest);
      }
    }

    if (!(error instanceof Error)) {
      return Promise.reject(new Error(JSON.stringify(error)));
    }
    return Promise.reject(error);
  },
);

interface HttpMethods {
  get<R, P>(path: string, _params?: P | null): Promise<R>;
  getBlob(path: string): Promise<Blob>;
  post<R, B>(path: string, body: B): Promise<R>;
  postMultipart<R>(path: string, formData: FormData): Promise<R>;
  putMultipart<R>(path: string, formData: FormData): Promise<R>;
  put<R, B>(path: string, id: IdAlias, body: B): Promise<R>;
  patch<R, B>(path: string, id: IdAlias, body: B): Promise<R>;
  delete<R, B>(path: string, id: IdAlias, body?: B | undefined): Promise<R>;
}

export const HttpClient: HttpMethods = {
  async get<R, P>(path: string, _params: P | null = null) {
    if (!_params) {
      return client.get<R>(path).then((response) => response.data);
    }

    const processedParams: Record<string, string | number> = {};
    const safeParams = Object.fromEntries(
      Object.entries(_params as Record<string, string | number>).filter(
        ([, value]) => value !== undefined,
      ),
    );
    Object.assign(processedParams, safeParams);

    const request: AxiosRequestConfig = {
      params: processedParams,
      paramsSerializer: {
        indexes: null,
      },
    };
    return client.get<R>(path, request).then((response) => response.data);
  },

  async getBlob(path: string): Promise<Blob> {
    return client
      .get(path, { responseType: 'blob' })
      .then((response) => response.data as Blob);
  },

  async post<R, B>(path: string, body: B) {
    return client.post<R>(path, body).then((response) => response.data);
  },

  async postMultipart<R>(path: string, formData: FormData) {
    return client
      .post<R>(path, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => response.data);
  },

  async putMultipart<R>(path: string, formData: FormData) {
    return client
      .put<R>(path, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => response.data);
  },

  async put<R, B>(path: string, id: IdAlias, body: B) {
    if (!id) {
      return client.put<R>(path, body).then((response) => response.data);
    }
    return client.put<R>(`${path}/${id}`, body).then((response) => response.data);
  },

  async patch<R, B>(path: string, id: IdAlias, body: B) {
    if (!id) {
      return client.patch<R>(path, body).then((response) => response.data);
    }
    return client.patch<R>(`${path}/${id}`, body).then((response) => response.data);
  },

  async delete<R, B>(path: string, id: IdAlias, body?: B) {
    if (!id) {
      return client.delete<R>(path, { data: body }).then((response) => response.data);
    }
    return client.delete<R>(`${path}/${id}`, { data: body }).then((response) => response.data);
  },
};
